/**
 * Baixa um grafico Highcharts como PNG sem depender do modulo de exportacao:
 * serializa o SVG renderizado, desenha num canvas (escala 2x) e dispara o download.
 */
export function downloadChartPng(chart, fileName = "grafico", options = {}) {
  if (!chart || !chart.container) return Promise.resolve(false);

  const svgElement = chart.container.querySelector("svg");
  if (!svgElement) return Promise.resolve(false);

  const clone = svgElement.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  // Fundo branco para projecao e impressao.
  const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  rect.setAttribute("width", "100%");
  rect.setAttribute("height", "100%");
  rect.setAttribute("fill", options.background || "#FFFFFF");
  clone.insertBefore(rect, clone.firstChild);

  const width = Number(svgElement.getAttribute("width")) || chart.chartWidth || 800;
  const height = Number(svgElement.getAttribute("height")) || chart.chartHeight || 400;
  const scale = options.scale || 2;
  const svgText = new XMLSerializer().serializeToString(clone);
  const svgUrl = URL.createObjectURL(new Blob([svgText], { type: "image/svg+xml;charset=utf-8" }));

  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const context = canvas.getContext("2d");
      context.scale(scale, scale);
      context.drawImage(image, 0, 0, width, height);
      URL.revokeObjectURL(svgUrl);
      canvas.toBlob((blob) => {
        if (!blob) return resolve(false);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${String(fileName).replace(/\.png$/i, "")}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        resolve(true);
      }, "image/png");
    };
    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      resolve(false);
    };
    image.src = svgUrl;
  });
}

// SVG renderizado de um grafico Highcharts como imagem carregada (para desenhar num canvas).
function chartImage(chart) {
  const svgElement = chart && chart.container && chart.container.querySelector("svg");
  if (!svgElement) return Promise.resolve(null);
  const clone = svgElement.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  const width = Number(svgElement.getAttribute("width")) || chart.chartWidth || 800;
  const height = Number(svgElement.getAttribute("height")) || chart.chartHeight || 400;
  const svgUrl = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml;charset=utf-8" }));
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(svgUrl); resolve({ image, width, height }); };
    image.onerror = () => { URL.revokeObjectURL(svgUrl); resolve(null); };
    image.src = svgUrl;
  });
}

// Reduz a fonte ate o texto caber na largura.
function fitFont(context, text, weight, size, maxWidth, minSize = 12) {
  let current = size;
  context.font = `${weight} ${current}px Avenir, Arial, sans-serif`;
  while (current > minSize && context.measureText(text).width > maxWidth) {
    current -= 1;
    context.font = `${weight} ${current}px Avenir, Arial, sans-serif`;
  }
  return current;
}

/**
 * Baixa um quadro unico em PNG: titulo, linha de numeros e graficos lado a lado,
 * cada um com o seu subtitulo (mesmo arranjo da tela de projecao).
 * stats: [{ value, label, color }]; panels: [{ title, chart, emptyText }].
 */
export async function downloadBoardPng({ title = "", stats = [], panels = [], fileName = "quadro", scale = 2 } = {}) {
  const width = 1600;
  const padding = 40;
  const gap = 24;
  const panelWidth = (width - padding * 2 - gap * (panels.length - 1)) / Math.max(panels.length, 1);
  const images = await Promise.all(panels.map((panel) => (panel.chart ? chartImage(panel.chart) : null)));
  const chartHeight = Math.max(240, ...images.filter(Boolean).map((item) => item.height * (panelWidth / item.width)));

  const titleTop = padding;
  const statsTop = titleTop + 56;
  const panelsTop = statsTop + (stats.length ? 110 : 0);
  const height = panelsTop + 36 + chartHeight + padding;

  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;
  const context = canvas.getContext("2d");
  context.scale(scale, scale);
  context.fillStyle = "#FFFFFF";
  context.fillRect(0, 0, width, height);
  context.textAlign = "center";
  context.textBaseline = "top";

  context.fillStyle = "#252538";
  fitFont(context, title, 700, 26, width - padding * 2);
  context.fillText(title, width / 2, titleTop);

  const statWidth = (width - padding * 2) / Math.max(stats.length, 1);
  stats.forEach((stat, index) => {
    const center = padding + statWidth * index + statWidth / 2;
    context.fillStyle = stat.color || "#252538";
    fitFont(context, String(stat.value), 700, 52, statWidth - 16);
    context.fillText(String(stat.value), center, statsTop);
    context.fillStyle = "#6B7280";
    fitFont(context, stat.label, 700, 15, statWidth - 16);
    context.fillText(stat.label, center, statsTop + 66);
  });

  panels.forEach((panel, index) => {
    const left = padding + (panelWidth + gap) * index;
    const center = left + panelWidth / 2;
    context.fillStyle = "#374151";
    fitFont(context, panel.title || "", 700, 18, panelWidth);
    context.fillText(panel.title || "", center, panelsTop);
    const item = images[index];
    if (item) {
      const drawHeight = item.height * (panelWidth / item.width);
      context.drawImage(item.image, left, panelsTop + 36, panelWidth, drawHeight);
    } else if (panel.emptyText) {
      context.fillStyle = "#9CA3AF";
      fitFont(context, panel.emptyText, 400, 15, panelWidth);
      context.fillText(panel.emptyText, center, panelsTop + 36 + chartHeight / 2);
    }
  });

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) return resolve(false);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${String(fileName).replace(/\.png$/i, "")}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      resolve(true);
    }, "image/png");
  });
}

export default { downloadChartPng, downloadBoardPng };
